'use client';

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface ErrorFallbackUIProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}

function ErrorFallbackUI({ error, errorInfo, onReset }: ErrorFallbackUIProps) {
  const t = useTranslations('errorBoundary');

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle>{t('global.title')}</CardTitle>
          </div>
          <CardDescription>{t('global.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {process.env.NODE_ENV === 'development' && error && (
            <div className="space-y-2">
              <div className="p-3 bg-muted rounded-md">
                <p className="text-form-help font-mono text-muted-foreground">
                  {error.toString()}
                </p>
              </div>
              {errorInfo && (
                <details className="cursor-pointer">
                  <summary className="text-form-help text-muted-foreground hover:text-foreground">
                    {t('viewStackTrace')}
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded-md text-card-meta overflow-auto max-h-64">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={onReset} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('global.tryAgain')}
            </Button>
            <Button
              onClick={() => (window.location.href = '/')}
              variant="outline"
            >
              {t('goHome')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FeatureErrorFallbackUI() {
  const t = useTranslations('errorBoundary');

  return (
    <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <p className="text-form-label">
          {t('component.failedToLoad', { componentName: '' })}
        </p>
      </div>
      <Button
        onClick={() => window.location.reload()}
        variant="ghost"
        size="sm"
        className="mt-2"
      >
        <RefreshCw className="h-3 w-3 mr-1" />
        {t('global.refreshPage')}
      </Button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <ErrorFallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

// Feature-level error boundary with simpler UI
export function FeatureErrorBoundary({ children, fallback }: Props) {
  return (
    <ErrorBoundary fallback={fallback || <FeatureErrorFallbackUI />}>
      {children}
    </ErrorBoundary>
  );
}
